import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {BulkActionFooter} from 'src/components/bulk-tool/bulk-action-footer';
import {dictionaryTreeToTreeStructure, getExportOptions, getPluginsAndApps} from 'src/components/features-section';
import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';
import {
    useLicenseDataModules,
    useLicenseFeatureModules,
    useLicenseFeatures,
    useSupportedLicenseTypes,
} from 'src/hooks/dictionaries';
import {licenseFeatureModuleToTreeStructure} from 'src/modules/create-account/steps/license-create/utils';
import {getAccountLicenses} from 'src/openapi/admin/apis/GetLicenseApi';
import {LicenseType as LicenseTypes} from 'src/openapi/admin/models/LicenseType';
import {useAppDispatch} from 'src/store/hooks';
import {isLicenseTypeProhibitedForCreation} from 'src/utils/license-util';

import Button from '@pb/kit-component-button';
import ComboBox, {ComboboxItem, DefaultItem} from '@pb/kit-component-combo-box';
import {DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {Group} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';
import {Scroll} from '@pb/kit-component-scroll';
import {pluralize} from '@pb/kit-formatters';

import {DataSetsSection} from '../../../profile/account/tabs/license-and-features/features-and-limits-tab/sections/access-to-modules/data-sets/data-sets-section';
import {PlatformFeatureModulesSection} from '../../../profile/account/tabs/license-and-features/features-and-limits-tab/sections/access-to-modules/platform-feature-modules/platform-feature-modules-section';
import {ExportOptionsSection} from '../../../profile/account/tabs/license-and-features/features-and-limits-tab/sections/access-to-modules/platform-features/export-options-section';
import {PluginsAppsSection} from '../../../profile/account/tabs/license-and-features/features-and-limits-tab/sections/access-to-modules/platform-features/plugins-apps-section';
import {ACCESS_DENI_MESSAGE, DISABLED_FOR_CHINA} from '../constants';
import {
    updateLicenseModalAccessModules,
    updateLicenseModalLicensePreset,
    updateLicenseModalLicensePresetMessage,
} from '../update-license-modal.module.styl';

import {TOOL_TITLE} from './constants';

import type {ProcessBulkAction} from 'src/components/bulk-tool/typedef';
import type {ActionOptions} from 'src/components/save-with-reason/action-with-reason-button';
import type {LicenseBulkUpdateRequest} from 'src/openapi/admin/models/LicenseBulkUpdateRequest';
import type {LicenseShortInfo} from 'src/openapi/admin/models/LicenseShortInfo';
import type {LicenseType} from 'src/openapi/admin/models/LicenseType';


type Props = {
    onCloseModal: (submit: boolean) => void,
    processAction: ProcessBulkAction<LicenseBulkUpdateRequest>,
    accountIds: number[],
};

export const DisableModuleLicenseModal = ({onCloseModal, processAction, accountIds}: Props) => {
    const apiDispatch = useAppDispatch();
    const {licenseFeatures} = useLicenseFeatures();
    const {licenseDataModules} = useLicenseDataModules();
    const {licenseFeaturesModules} = useLicenseFeatureModules();
    const [dataSets, setDataSets] = useState<Array<string>>([]);
    const [platformFeatureModules, setPlatformFeatureModules] = useState<Array<string>>([]);
    const [platformFeatures, setPlatformFeatures] = useState<Array<string>>([]);
    const [licenseInfo, setLicenseInfo] = useState<LicenseShortInfo[] | null>(null);
    const [licenseType, setLicenseType] = useState<string>('');
    const [isUpdateDisabled, setUpdateDisabled] = useState<boolean>(false);
    const {data: supportedLicenseTypes} = useSupportedLicenseTypes({});
    const {hasAccess} = useAdminAccessRight();
    const isRestrictedForChina = !hasAccess(ACCESS_RIGHTS.TEMPORARY_RESTRICTION_CHINA);

    useEffect(() => {
        const fetchData = async () => {
            setLicenseInfo(null);
            const response = await apiDispatch(getAccountLicenses(
                {
                    requestBody: accountIds,
                    licenseType: licenseType ? licenseType as LicenseType : undefined,
                },
            ));
            if (response.payload.success) {
                setLicenseInfo(response.payload.data);
            }
        };

        fetchData();
    }, [accountIds, licenseType, apiDispatch]);

    useEffect(() => {
        let totalChildrenCount = 0;
        licenseDataModules.forEach(module => {
            if (module.children) {
                totalChildrenCount += module.children.length;
            }
        });

        if (dataSets.length >= totalChildrenCount && platformFeatureModules.length >= licenseFeaturesModules.length) {
            setUpdateDisabled(true);
        } else {
            setUpdateDisabled(false);
        }
    }, [dataSets, platformFeatureModules, licenseDataModules, licenseFeaturesModules.length]);

    const disableModules = useCallback(async (options?: ActionOptions) => {
        if (licenseInfo) {
            const licenseIds = licenseInfo
                .filter((license): license is LicenseShortInfo & {
                    licenseId: number,
                } => license.licenseId !== undefined)
                .map(license => license.licenseId);

            return await processAction({
                licenseIds,
                dataModules: dataSets,
                featureModules: platformFeatureModules,
                availableFeatures: platformFeatures,
            }, options);
        }
    }, [licenseInfo, dataSets, platformFeatureModules, platformFeatures, processAction]);

    const message = useMemo(() => {
        let text = 'The input parameters are analyzed...';
        if (licenseInfo) {
            if (licenseInfo.length === 0) {
                text = 'The selected accounts have no active or pending licenses';
            } else {
                text = `${licenseInfo.length} ${pluralize('license[s]', licenseInfo.length)} will be affected.`;
            }
        }

        return text;
    }, [licenseInfo]);

    const updatedLicenseFeaturesModules = useMemo(() => licenseFeaturesModules.map(item => {
        if (isRestrictedForChina && DISABLED_FOR_CHINA.includes(item.code)) {
            return {...item, disabled: true, explain: ACCESS_DENI_MESSAGE, explainMaxWidth: 300, checked: false};
        }
        return {...item, disabled: false, checked: false};
    }), [licenseFeaturesModules, isRestrictedForChina]);

    const filteredLicenseTypes = useMemo(() => {


        return supportedLicenseTypes.filter(item => (
            !isLicenseTypeProhibitedForCreation(item.code as LicenseTypes)
        ));
    }, [supportedLicenseTypes]);

    const isActionDisabled = isUpdateDisabled || !(
        licenseInfo && licenseInfo.length > 0 &&
        (dataSets.length > 0 || platformFeatureModules.length > 0 || platformFeatures.length > 0)
    );

    return (
        <>
            <DialogHeader onClose={() => onCloseModal(false)}>
                {TOOL_TITLE}
            </DialogHeader>
            <DialogMain>
                <div className={updateLicenseModalLicensePreset}>
                    <div style={{width: 300}}>
                        <Label htmlFor="license-type-combobox">License Type</Label>
                        <ComboBox
                            id="license-type-combobox"
                            disabled={false}
                            value={licenseType}
                            onChange={setLicenseType}>
                            {[
                                <DefaultItem
                                    key="all"
                                    caption="All license types"
                                    value=""
                                />,
                                ...filteredLicenseTypes.map(item => (
                                    <ComboboxItem key={item.code} value={item.code} caption={item.description}/>
                                )),
                            ]}
                        </ComboBox>
                    </div>
                    <div className={updateLicenseModalLicensePresetMessage}>
                        <strong>{message}</strong>
                    </div>
                </div>
                <Scroll>
                    <div className={updateLicenseModalAccessModules}>
                        <Group gap="xxl" alignItems="start">
                            <DataSetsSection
                                loading={false}
                                dataSetsTree={dictionaryTreeToTreeStructure(
                                    licenseDataModules,
                                    !licenseInfo || licenseInfo.length === 0,
                                )}
                                dataSetsIds={dataSets}
                                onChange={setDataSets}
                            />
                            <PlatformFeatureModulesSection {...{
                                loading: false,
                                platformFeatureModulesTree: licenseFeatureModuleToTreeStructure(
                                    updatedLicenseFeaturesModules,
                                    !licenseInfo || licenseInfo.length === 0,
                                ),
                                platformFeatureModules,
                                onChange: (newPlatformFeatureModules: Array<string>) => {
                                    setPlatformFeatureModules(newPlatformFeatureModules);
                                },
                            }}
                            />
                            <Group direction="v">
                                <PluginsAppsSection {...{
                                    loading: false,
                                    pluginsAppsTree: licenseFeatureModuleToTreeStructure(
                                        getPluginsAndApps(
                                            licenseFeatures.map(item => ({...item, disabled: false, checked: false})),
                                        ),
                                        !licenseInfo || licenseInfo.length === 0,
                                    ),
                                    platformFeatures,
                                    onChange: (newPlatformFeatures: Array<string>) => {
                                        setPlatformFeatures(newPlatformFeatures);
                                    },
                                }}
                                />
                                <ExportOptionsSection {...{
                                    loading: false,
                                    exportOptionsTree: licenseFeatureModuleToTreeStructure(
                                        getExportOptions(
                                            licenseFeatures.map(item => ({...item, disabled: false, checked: false})),
                                        ),
                                        !licenseInfo || licenseInfo.length === 0,
                                    ),
                                    platformFeatures,
                                    disabled: !licenseInfo || licenseInfo.length === 0,
                                    onChange: (newExportOptions: Array<string>) => {
                                        setPlatformFeatures(newExportOptions);
                                    },
                                }}
                                />
                            </Group>
                        </Group>
                    </div>
                </Scroll>
            </DialogMain>
            <BulkActionFooter
                action="Update"
                onConfirm={disableModules}
                busy={!licenseInfo}
                disabled={isActionDisabled}>
                <Group>

                    <Button variant="Type.SECONDARY" onClick={() => onCloseModal(true)}>
                        Cancel
                    </Button>
                </Group>
            </BulkActionFooter>
        </>
    );
};
